/**
 * Onboarding Step Indicator
 *
 * Visual progress indicator showing current step in the onboarding flow.
 * Clean, minimal design with dots and connecting lines.
 */

import * as React from 'react';
import { CheckIcon } from '@newcore/icons';
import { cn } from '@newcore/ui/cn';
import { FontWeights } from '@newcore/ui/enums/font-weights';
import { TextSizes } from '@newcore/ui/enums/typography-text-sizes';
import { textVariants } from '@newcore/ui/variants/typography-text-variants';
import type { OnboardingStepIndicatorProps } from './types';

/**
 * Step indicator with clean, minimal design
 *
 * - Current step: filled primary with subtle ring
 * - Completed steps: subtle checkmark
 * - Future steps: muted outline
 *
 * @example
 * <Onboarding.StepIndicator current={2} total={4} />
 */
export const OnboardingStepIndicator: React.FC<
  OnboardingStepIndicatorProps
> = ({
  current,
  total,
  allowClickNavigation = false,
  onStepClick,
  className,
}) => {
  const handleStepClick = (step: number) => {
    if (allowClickNavigation && onStepClick) {
      onStepClick(step);
    }
  };

  return (
    <div className={cn('flex items-center justify-center', className)}>
      {Array.from({ length: total }, (_, index) => {
        const step = index + 1;
        const isActive = step === current;
        const isCompleted = step < current;
        const isClickable = allowClickNavigation && (isCompleted || isActive);

        return (
          <React.Fragment key={step}>
            {/* Step circle */}
            <button
              type='button'
              onClick={() => handleStepClick(step)}
              disabled={!isClickable}
              className={cn(
                'relative flex items-center justify-center rounded-full',
                'h-8 w-8',
                textVariants({
                  size: TextSizes.XS,
                  weight: FontWeights.SEMIBOLD,
                }),
                'transition-all duration-300',
                // Active step: primary with ring effect
                isActive && [
                  'bg-primary-solid text-primary_on-brand',
                  'ring-4 ring-[var(--color-bg-primary-solid)]/10',
                ],
                // Completed steps: subtle filled with check
                isCompleted && 'bg-primary-solid/10 text-primary',
                // Future steps: light outline
                !isActive &&
                  !isCompleted && [
                    'bg-transparent',
                    // No semantic token matches text-tertiary at low opacity.
                    'border-2 border-[var(--color-text-tertiary)]/20',
                    'text-tertiary/50',
                  ],
                // Hover effects
                isClickable && 'cursor-pointer',
                isClickable && isCompleted && 'hover:bg-primary-solid/20',
                isClickable &&
                  isActive &&
                  'hover:ring-[var(--color-bg-primary-solid)]/20',
                !isClickable && 'cursor-default'
              )}
              aria-label={`Step ${step}${isActive ? ' (current)' : ''}${isCompleted ? ' (completed)' : ''}`}
              aria-current={isActive ? 'step' : undefined}
            >
              {isCompleted ? (
                <CheckIcon className='h-3.5 w-3.5 stroke-[3]' />
              ) : (
                step
              )}
            </button>

            {/* Connecting line (not after last step) */}
            {step < total && (
              <div
                className={cn(
                  'h-[2px] w-6 transition-colors duration-300',
                  step < current
                    ? 'bg-primary-solid/20'
                    : // No semantic token matches text-tertiary at low opacity.
                      'bg-[var(--color-text-tertiary)]/10'
                )}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
